import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const BatchCard = ({ batch, onPause, onResume, onDownload, onViewDetails }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case "processing":
        return {
          color: "text-accent",
          bgColor: "bg-accent/10",
          borderColor: "border-accent/20",
          icon: "Loader2",
        };
      case "completed":
        return {
          color: "text-success",
          bgColor: "bg-success/10",
          borderColor: "border-success/20",
          icon: "CheckCircle",
        };
      case "failed":
        return {
          color: "text-error",
          bgColor: "bg-error/10",
          borderColor: "border-error/20",
          icon: "AlertCircle",
        };
      case "paused":
        return {
          color: "text-warning",
          bgColor: "bg-warning/10",
          borderColor: "border-warning/20",
          icon: "Pause",
        };
      default:
        return {
          color: "text-muted-foreground",
          bgColor: "bg-muted",
          borderColor: "border-border",
          icon: "Circle",
        };
    }
  };

  const statusConfig = getStatusConfig(batch?.status);
  const progressPercentage = Math.round(
    (batch?.processedCount / batch?.totalCount) * 100
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div
            className={`
            flex items-center justify-center w-10 h-10 rounded-lg border
            ${statusConfig?.bgColor} ${statusConfig?.borderColor}
          `}
          >
            <Icon
              name={statusConfig?.icon}
              size={20}
              color="currentColor"
              className={`${statusConfig?.color} ${
                batch?.status === "processing" ? "animate-spin" : ""
              }`}
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {batch?.batchName || `Batch ${batch?.id}`}
            </h3>
            <p className="text-sm text-muted-foreground">
              Uploaded {batch?.uploadTime}
            </p>
          </div>
        </div>

        <div
          className={`
          px-3 py-1 rounded-full text-sm font-medium border
          ${statusConfig?.bgColor} ${statusConfig?.borderColor} ${statusConfig?.color}
        `}
        >
          {batch?.status?.charAt(0)?.toUpperCase() + batch?.status?.slice(1)}
        </div>
      </div>
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">
            Progress: {batch?.processedCount}/{batch?.totalCount} invoices
          </span>
          <span className="text-sm font-semibold text-foreground">
            {progressPercentage}%
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              batch?.status === "completed"
                ? "bg-success"
                : batch?.status === "failed"
                ? "bg-error"
                : batch?.status === "paused"
                ? "bg-warning"
                : "bg-accent"
            }`}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
      {/* Statistics */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <div className="text-lg font-semibold text-success">
            {batch?.successCount}
          </div>
          <div className="text-xs text-muted-foreground">Success</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-error">
            {batch?.failureCount}
          </div>
          <div className="text-xs text-muted-foreground">Failed</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-foreground">
            {batch?.estimatedTime}
          </div>
          <div className="text-xs text-muted-foreground">ETA</div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center space-x-2">
        {batch?.status === "processing" && (
          <Button
            variant="outline"
            size="sm"
            iconName="Pause"
            iconPosition="left"
            onClick={() => onPause(batch?.id)}
          >
            Pause
          </Button>
        )}

        {batch?.status === "paused" && (
          <Button
            variant="default"
            size="sm"
            iconName="Play"
            iconPosition="left"
            onClick={() => onResume(batch?.id)}
          >
            Resume
          </Button>
        )}

        {(batch?.status === "completed" || batch?.status === "failed") && (
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            onClick={() => onDownload(batch?.id)}
          >
            Download
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          iconName="Eye"
          iconPosition="left"
          onClick={() => onViewDetails(batch?.id)}
        >
          Details
        </Button>
      </div>
    </div>
  );
};

export default BatchCard;
